import React, { useCallback, useEffect, useState } from "react";
import { useApp } from "../context/AppContext";
import { useAuth } from "../context/AuthContext";
import { adminApi } from "../services/api";
import EmptyState from "../components/EmptyState";
import Modal from "../components/Modal";
import { colorFor, formatDate, initials, timeAgo } from "../utils/format";

const ACCESS_LABELS = {
  none: { label: "No access", tag: "" },
  pending: { label: "Requested", tag: "tag-warning" },
  approved: { label: "Has access", tag: "tag-success" },
  rejected: { label: "Rejected", tag: "tag-danger" },
  revoked: { label: "Revoked", tag: "tag-danger" },
};

const FILTERS = [
  { id: "all", label: "All", params: {} },
  { id: "approved", label: "Has access", params: { access: "approved" } },
  { id: "pending", label: "Requested", params: { access: "pending" } },
  { id: "none", label: "No access", params: { access: "none" } },
  { id: "revoked", label: "Revoked", params: { access: "revoked" } },
  { id: "rejected", label: "Rejected", params: { access: "rejected" } },
  { id: "admin", label: "Admins", params: { role: "admin" } },
  { id: "suspended", label: "Suspended", params: { suspended: "true" } },
  { id: "unverified", label: "Unconfirmed email", params: { verified: "false" } },
];

const BOOLEAN_FIELDS = ["disabled", "emailVerified"];

const DEFAULT_CREDITS = 100;
const creditsLeft = (user) => Math.max(0, (user.access?.credits ?? 0) - (user.access?.creditsUsed ?? 0));
const hasCredits = (user) => user.role === "user" && user.access?.status === "approved";

function AccessTag({ user }) {
  if (user.role === "superadmin") return <span className="tag tag-info">Superadmin</span>;
  if (user.role === "admin") return <span className="tag tag-info">Admin</span>;
  if (user.access?.wantsMore) return <span className="tag tag-warning">Wants more credits</span>;
  if (hasCredits(user) && creditsLeft(user) === 0) return <span className="tag tag-warning">Out of credits</span>;
  const { label, tag } = ACCESS_LABELS[user.access?.status] || ACCESS_LABELS.none;
  return <span className={`tag ${tag}`}>{label}</span>;
}

function CreditsInput({ id, value, onChange }) {
  return (
    <div className="input-group input-group-sm flex-shrink-0" style={{ width: 150 }}>
      <input
        id={id}
        type="number"
        className="form-control"
        min={1}
        max={10000}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        aria-label="AI credits to give"
      />
      <span className="input-group-text">credits</span>
    </div>
  );
}

function UserName({ user, me }) {
  return (
    <div className="d-flex align-items-center gap-2 min-w-0">
      <span className="avatar avatar-sm" style={{ background: colorFor(user.name) }}>
        {initials(user.name)}
      </span>
      <div className="min-w-0">
        <div className="fw-medium text-truncate">
          {user.name}
          {user.id === me && <span className="text-muted fw-normal"> (you)</span>}
          {user.disabled && <span className="tag tag-danger ms-2">Suspended</span>}
          {!user.emailVerified && (
            <span className="tag tag-warning ms-2" title="Hasn't clicked the link in the confirmation email yet">
              Unconfirmed
            </span>
          )}
        </div>
        <div className="small text-muted text-truncate">{user.email}</div>
      </div>
    </div>
  );
}

function Stats({ stats }) {
  if (!stats) return null;
  const cards = [
    { label: "Users", value: stats.users, hint: `${stats.newThisWeek} new this week`, icon: "bi-people", color: "#708090" },
    { label: "Access requests", value: stats.pending, hint: "Waiting for you", icon: "bi-hourglass-split", color: "#f59e0b" },
    { label: "With AI access", value: stats.approved, hint: `${stats.suspended} suspended`, icon: "bi-unlock", color: "#10b981" },
    {
      label: "AI calls today",
      value: stats.usage.aiToday,
      hint: `${stats.usage.ai30} in 30 days`,
      icon: "bi-stars",
      color: "#ec4899",
    },
  ];
  return (
    <div className="stat-grid mb-4">
      {cards.map((c) => (
        <div key={c.label} className="stat-card" style={{ "--stage-color": c.color }}>
          <div className="d-flex justify-content-between align-items-center">
            <span className="small text-muted">{c.label}</span>
            <i className={`bi ${c.icon} stat-icon`} />
          </div>
          <div className="stat-value">{c.value}</div>
          <div className="stat-hint">{c.hint}</div>
        </div>
      ))}
    </div>
  );
}

function RequestCard({ user, defaultCredits, onDecide }) {
  const [note, setNote] = useState("");
  const [credits, setCredits] = useState(defaultCredits);
  const [busy, setBusy] = useState("");
  const more = user.access.wantsMore;

  const decide = async (action) => {
    setBusy(action);
    await onDecide(user, action, note.trim(), Number(credits));
    setBusy("");
  };

  return (
    <div className="card">
      <div className="card-body">
        <div className="d-flex flex-column flex-md-row gap-3 justify-content-between">
          <UserName user={user} />
          <div className="small text-muted text-md-end flex-shrink-0">
            {more ? "Asked for more credits" : "Requested"} {timeAgo(user.access.requestedAt)}
            <div>
              {more
                ? `Used all ${user.access.credits} credits · ${user.jobs} jobs`
                : `Joined ${formatDate(user.createdAt, { month: "short", day: "numeric", year: "numeric" })} · ${user.jobs} jobs`}
            </div>
          </div>
        </div>
        <blockquote className="request-reason my-3">{user.access.reason || <span className="text-muted">No reason given</span>}</blockquote>
        <div className="d-flex flex-column flex-md-row gap-2">
          <input
            className="form-control form-control-sm"
            placeholder="Optional note they'll see (e.g. Approved for 2 weeks)"
            maxLength={200}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            aria-label={`Note for ${user.name}`}
          />
          <div className="d-flex gap-2 flex-shrink-0">
            <CreditsInput value={credits} onChange={setCredits} />
            <button className="btn btn-success btn-sm" disabled={!!busy || !(Number(credits) >= 1)} onClick={() => decide("approve")}>
              {busy === "approve" ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-check-lg me-1" />}
              {more ? "Give credits" : "Approve"}
            </button>
            <button className="btn btn-outline-danger btn-sm" disabled={!!busy} onClick={() => decide("reject")}>
              {busy === "reject" ? <span className="spinner-border spinner-border-sm me-1" /> : <i className="bi bi-x-lg me-1" />}
              {more ? "Turn down" : "Reject"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Requests({ onDecide, version, defaultCredits }) {
  const { toast } = useApp();
  const [users, setUsers] = useState(null);

  useEffect(() => {
    let live = true;
    adminApi
      .users({ access: "pending" })
      .then((data) => live && setUsers(data.users))
      .catch((err) => toast(err.message, "danger"));
    return () => {
      live = false;
    };
  }, [toast, version]);

  if (!users) return <Loading />;
  if (users.length === 0) {
    return (
      <div className="card">
        <EmptyState
          icon="bi-inbox"
          title="No requests waiting"
          text="New access requests, and people asking for more AI credits, show up here oldest first."
        />
      </div>
    );
  }
  return (
    <div className="d-flex flex-column gap-2">
      {users.map((u) => (
        <RequestCard key={u.id} user={u} defaultCredits={defaultCredits} onDecide={onDecide} />
      ))}
    </div>
  );
}

function Loading() {
  return (
    <div className="text-center py-5">
      <span className="spinner-border text-primary" role="status" />
    </div>
  );
}

function UsersTable({ me, onEdit, version }) {
  const { toast } = useApp();
  const [query, setQuery] = useState("");
  const [search, setSearch] = useState("");
  const [filter, setFilter] = useState("all");
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);

  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(query.trim());
      setPage(1);
    }, 300);
    return () => clearTimeout(t);
  }, [query]);

  useEffect(() => {
    let live = true;
    const params = { ...FILTERS.find((f) => f.id === filter).params, page };
    if (search) params.q = search;
    adminApi
      .users(params)
      .then((res) => live && setData(res))
      .catch((err) => toast(err.message, "danger"));
    return () => {
      live = false;
    };
  }, [search, filter, page, toast, version]);

  const pages = data ? Math.max(1, Math.ceil(data.total / data.pageSize)) : 1;

  return (
    <>
      <div className="d-flex flex-column flex-md-row gap-2 mb-3">
        <div className="input-group tracker-search">
          <span className="input-group-text bg-white">
            <i className="bi bi-search" />
          </span>
          <input
            className="form-control"
            placeholder="Search by name or email"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Search users"
          />
        </div>
        <div className="chip-scroll ms-md-auto">
          {FILTERS.map((f) => (
            <button
              key={f.id}
              className={`chip ${filter === f.id ? "active" : ""}`}
              onClick={() => {
                setFilter(f.id);
                setPage(1);
              }}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {!data ? (
        <Loading />
      ) : data.users.length === 0 ? (
        <div className="card">
          <EmptyState icon="bi-funnel" title="No users match" text="Try a different search or filter." />
        </div>
      ) : (
        <div className="card">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0 admin-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Access</th>
                  <th className="text-end">Jobs</th>
                  <th className="text-end" title="AI calls today / last 30 days">
                    AI today / 30d
                  </th>
                  <th>Joined</th>
                  <th>Last login</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {data.users.map((u) => (
                  <tr key={u.id} onClick={() => onEdit(u)} role="button">
                    <td style={{ maxWidth: 280 }}>
                      <UserName user={u} me={me} />
                    </td>
                    <td>
                      <AccessTag user={u} />
                      {hasCredits(u) && (
                        <div className="small text-muted text-nowrap mt-1">
                          {creditsLeft(u)} of {u.access.credits} credits left
                        </div>
                      )}
                    </td>
                    <td className="text-end">{u.jobs}</td>
                    <td className="text-end">
                      {u.usage.aiToday} / {u.usage.ai30}
                    </td>
                    <td className="small text-muted text-nowrap">
                      {formatDate(u.createdAt, { month: "short", day: "numeric", year: "numeric" })}
                    </td>
                    <td className="small text-muted text-nowrap">{u.lastLoginAt ? timeAgo(u.lastLoginAt) : "Never"}</td>
                    <td className="text-end">
                      <button
                        className="btn btn-light btn-sm"
                        onClick={(e) => {
                          e.stopPropagation();
                          onEdit(u);
                        }}
                      >
                        Edit
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="card-footer bg-white d-flex align-items-center justify-content-between small text-muted">
            <span>
              {data.total} user{data.total === 1 ? "" : "s"}
            </span>
            {pages > 1 && (
              <div className="d-flex align-items-center gap-2">
                <button className="btn btn-light btn-sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>
                  <i className="bi bi-chevron-left" />
                </button>
                Page {page} of {pages}
                <button className="btn btn-light btn-sm" disabled={page >= pages} onClick={() => setPage((p) => p + 1)}>
                  <i className="bi bi-chevron-right" />
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function EditUserModal({ user, me, limits, onClose, onSaved, onDeleted }) {
  const { toast } = useApp();
  const self = user.id === me;
  const [credits, setCredits] = useState(limits?.aiCredits ?? DEFAULT_CREDITS);
  const [form, setForm] = useState({
    name: user.name,
    email: user.email,
    role: user.role,
    disabled: !!user.disabled,
    emailVerified: !!user.emailVerified,
  });
  const [note, setNote] = useState(user.access?.note || "");
  const [busy, setBusy] = useState("");
  const [confirmDelete, setConfirmDelete] = useState("");
  const status = user.access?.status || "none";

  const run = async (key, fn) => {
    setBusy(key);
    try {
      await fn();
    } catch (err) {
      toast(err.message, "danger");
    }
    setBusy("");
  };

  const changes = Object.fromEntries(Object.entries(form).filter(([k, v]) => v !== (BOOLEAN_FIELDS.includes(k) ? !!user[k] : user[k])));
  const emailEdited = form.email.trim().toLowerCase() !== user.email;
  const save = () =>
    run("save", async () => {
      const body = emailEdited ? { ...changes, emailVerified: form.emailVerified } : changes;
      const { user: saved } = await adminApi.updateUser(user.id, body);
      onSaved(saved, "Saved");
    });

  const setAccess = (action, message) =>
    run(action, async () => {
      const { user: saved } = await adminApi.setAccess(user.id, action, note.trim(), action === "approve" ? Number(credits) : undefined);
      onSaved(saved, message);
    });

  const remove = () =>
    run("delete", async () => {
      await adminApi.deleteUser(user.id);
      onDeleted(user);
    });

  const more = status === "approved" && user.access?.wantsMore;
  const approveLabel = more ? "Give more credits" : status === "approved" ? "Start fresh credits" : status === "pending" ? "Approve request" : "Give access";
  const accessButtons = [
    {
      action: "approve",
      label: approveLabel,
      variant: status === "approved" && !more ? "btn-outline-success" : "btn-success",
      message: status === "approved" ? "Credits given" : "Access granted",
      title: status === "approved" && !more ? "Replaces what's left with a fresh allowance" : undefined,
    },
    (status === "pending" || more) && { action: "reject", label: more ? "Turn down" : "Reject", variant: "btn-outline-danger", message: "Request turned down" },
    status === "approved" && { action: "revoke", label: "Revoke access", variant: "btn-outline-danger", message: "Access revoked" },
    status !== "none" && { action: "reset", label: "Reset", variant: "btn-light", message: "Access reset", title: "Back to no access, with no request on file" },
  ].filter(Boolean);

  return (
    <Modal
      show
      onClose={onClose}
      title="Edit user"
      size="modal-lg"
      footer={
        <>
          <button className="btn btn-light" onClick={onClose}>
            Close
          </button>
          <button className="btn btn-primary" onClick={save} disabled={!!busy || Object.keys(changes).length === 0 || !form.name.trim() || !form.email.trim()}>
            {busy === "save" && <span className="spinner-border spinner-border-sm me-2" />}
            Save changes
          </button>
        </>
      }
    >
      <div className="row g-3">
        <div className="col-md-6">
          <label className="form-label small fw-medium" htmlFor="admin-name">
            Name
          </label>
          <input id="admin-name" className="form-control" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
        </div>
        <div className="col-md-6">
          <label className="form-label small fw-medium" htmlFor="admin-email">
            Email
          </label>
          <input
            id="admin-email"
            type="email"
            className="form-control"
            value={form.email}
            onChange={(e) => {
              const edited = e.target.value.trim().toLowerCase() !== user.email;
              setForm({ ...form, email: e.target.value, emailVerified: edited ? false : !!user.emailVerified });
            }}
          />
          <div className="form-check mt-2">
            <input
              id="admin-verified"
              className="form-check-input"
              type="checkbox"
              checked={form.emailVerified}
              onChange={(e) => setForm({ ...form, emailVerified: e.target.checked })}
            />
            <label className="form-check-label small" htmlFor="admin-verified">
              Email confirmed
            </label>
            {emailEdited && !form.emailVerified && (
              <div className="form-text">We'll email the new address a confirmation link.</div>
            )}
          </div>
        </div>
        <div className="col-md-6">
          <label className="form-label small fw-medium" htmlFor="admin-role">
            Role
          </label>
          <select
            id="admin-role"
            className="form-select"
            value={form.role}
            disabled={self}
            onChange={(e) => setForm({ ...form, role: e.target.value })}
          >
            <option value="user">User</option>
            <option value="admin">Admin (uses the app + /admin)</option>
            <option value="superadmin">Superadmin (ops only — no job hunting)</option>
          </select>
          {self && <div className="form-text">You can&apos;t change your own role.</div>}
        </div>
        <div className="col-md-6 d-flex align-items-end">
          <div className="form-check form-switch mb-2">
            <input
              id="admin-disabled"
              className="form-check-input"
              type="checkbox"
              role="switch"
              checked={form.disabled}
              disabled={self}
              onChange={(e) => setForm({ ...form, disabled: e.target.checked })}
            />
            <label className="form-check-label" htmlFor="admin-disabled">
              Suspended (can't sign in or use the extension)
            </label>
          </div>
        </div>
      </div>

      <hr className="my-4" />

      <h6 className="mb-2 d-flex align-items-center gap-2">
        AI access <AccessTag user={user} />
      </h6>
      {user.role === "admin" || user.role === "superadmin" ? (
        <p className="small text-muted mb-0">
          {user.role === "superadmin"
            ? "Superadmins maintain the app from the ops console and don't use job-seeker AI credits."
            : "Admins always have access to every feature, with no credit or monthly limits."}
        </p>
      ) : (
        <>
          <div className="small text-muted mb-2">
            {user.access?.requestedAt && <>Requested {formatDate(user.access.requestedAt)}. </>}
            {user.access?.decidedAt && <>Last changed {formatDate(user.access.decidedAt)}. </>}
            {hasCredits(user) && (
              <>
                Used {user.access.creditsUsed} of {user.access.credits} AI credits.{" "}
              </>
            )}
            {user.usage && (
              <>
                {user.usage.ai30} AI calls in the last 30 days.
              </>
            )}
          </div>
          {user.access?.reason && <blockquote className="request-reason mb-3">{user.access.reason}</blockquote>}
          <input
            className="form-control form-control-sm mb-2"
            placeholder="Optional note they'll see"
            maxLength={200}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            aria-label="Note for the user"
          />
          <div className="d-flex flex-wrap gap-2">
            <CreditsInput id="admin-credits" value={credits} onChange={setCredits} />
            {accessButtons.map((b) => (
              <button
                key={b.action}
                className={`btn btn-sm ${b.variant}`}
                title={b.title}
                disabled={!!busy || (b.action === "approve" && !(Number(credits) >= 1))}
                onClick={() => setAccess(b.action, b.message)}
              >
                {busy === b.action && <span className="spinner-border spinner-border-sm me-1" />}
                {b.label}
              </button>
            ))}
          </div>
        </>
      )}

      {!self && (
        <>
          <hr className="my-4" />
          <h6 className="text-danger mb-2">Delete account</h6>
          <p className="small text-muted mb-2">
            Permanently deletes {user.name}'s account, jobs, documents and contacts. Type their email to confirm.
          </p>
          <div className="d-flex gap-2">
            <input
              className="form-control form-control-sm"
              placeholder={user.email}
              value={confirmDelete}
              onChange={(e) => setConfirmDelete(e.target.value)}
              aria-label="Type the email to confirm deletion"
            />
            <button className="btn btn-danger btn-sm flex-shrink-0" disabled={!!busy || confirmDelete.trim().toLowerCase() !== user.email} onClick={remove}>
              {busy === "delete" && <span className="spinner-border spinner-border-sm me-1" />}
              Delete
            </button>
          </div>
        </>
      )}
    </Modal>
  );
}

function Admin() {
  const { toast, refreshStatus } = useApp();
  const { user: me, setUser } = useAuth();
  const [tab, setTab] = useState("requests");
  const [stats, setStats] = useState(null);
  const [editing, setEditing] = useState(null);
  const [version, setVersion] = useState(0);

  useEffect(() => {
    adminApi
      .stats()
      .then(setStats)
      .catch((err) => toast(err.message, "danger"));
  }, [toast, version]);

  const changed = useCallback(() => {
    setVersion((v) => v + 1);
    refreshStatus();
  }, [refreshStatus]);

  const decide = async (user, action, note, credits) => {
    try {
      await adminApi.setAccess(user.id, action, note, action === "approve" ? credits : undefined);
      toast(
        action === "approve"
          ? `${user.name} has ${credits} AI credits`
          : user.access.wantsMore
            ? `Turned down ${user.name}'s request for more credits`
            : `Rejected ${user.name}'s request`
      );
      changed();
    } catch (err) {
      toast(err.message, "danger");
    }
  };

  const saved = (user, message) => {
    toast(message);
    setEditing((prev) => (prev ? { ...prev, ...user } : prev));
    if (user.id === me.id) setUser(user);
    changed();
  };

  const deleted = (user) => {
    toast(`Deleted ${user.name}`);
    setEditing(null);
    changed();
  };

  const tabs = [
    { id: "requests", label: "Access requests", icon: "bi-inbox", count: stats?.pending },
    { id: "users", label: "All users", icon: "bi-people" },
  ];

  return (
    <>
      <div className="page-header">
        <div>
          <h1 className="page-title">Admin</h1>
          <p className="page-subtitle">Approve AI access and manage accounts.</p>
        </div>
      </div>

      <Stats stats={stats} />

      <div className="tab-scroll mb-3">
        {tabs.map((t) => (
          <button key={t.id} className={`tab-btn ${tab === t.id ? "active" : ""}`} onClick={() => setTab(t.id)}>
            <i className={`bi ${t.icon}`} />
            {t.label}
            {t.count > 0 && <span className="side-badge">{t.count}</span>}
          </button>
        ))}
      </div>

      {tab === "requests" ? (
        <Requests onDecide={decide} version={version} defaultCredits={stats?.limits?.aiCredits ?? DEFAULT_CREDITS} />
      ) : (
        <UsersTable me={me.id} onEdit={setEditing} version={version} />
      )}

      {editing && (
        <EditUserModal
          key={editing.id + (editing.updatedAt || "")}
          user={editing}
          me={me.id}
          limits={stats?.limits}
          onClose={() => setEditing(null)}
          onSaved={saved}
          onDeleted={deleted}
        />
      )}
    </>
  );
}

export default Admin;
